import { Card, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Formik } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useUpdateWorkflow from "@/hooks/requests/wokflow/use-update-workflow";
import useUpdateWorkflowAvatar from "@/hooks/requests/wokflow/use-update-workflow-avatar";
import useMutate from "@/hooks/use-mutate";
import { IWorkflow, IWorkflowInput } from "@/interfaces/workflow.interface";
import { WorkflowEditDialogMessages } from "@/messages/workflow/workflow-edit-dialog.messages";

import AgentAvatarImage from "./components/AgentAvatarImage";
import {
  WorkflowFormValues,
  buildWorkflowFormInitialData,
  hasAvatarChanged,
  normalizeWorkflowFormData,
} from "./form-data";

type Props = {
  messages: WorkflowEditDialogMessages;
  workflow: IWorkflow | IWorkflowInput | null;
};

export default function WorkflowFormTab({ messages, workflow }: Props) {
  const { lang } = useLanguage();
  const router = useNavigate();
  const { update, loadingIcon, isLoading: isUpdatingWorkflow } = useUpdateWorkflow();
  const { update: updateAvatar, isLoading: isUpdatingAvatar } = useUpdateWorkflowAvatar();
  const { mutate } = useMutate();
  const isLoading = isUpdatingWorkflow || isUpdatingAvatar;

  const handleUpdate = async ({ avatar, ...formData }: WorkflowFormValues) => {
    if (!workflow) return;

    const _id = Number(workflow.id) || 0;
    const successMessage = messages.workflowUpdateSuccess;
    const errorMessage = messages.workflowUpdateError;

    const onSuccess = () => {
      mutate(SWR_KEYS.getWorkflowById);
      mutate(SWR_KEYS.getWorkflows);
      router(ROUTE_URLS.workflow(lang));
    };

    try {
      if (hasAvatarChanged(avatar, workflow)) {
        await updateAvatar({ _id, formData: avatar, errorMessage: messages.avatarUpdateError });
      }

      const sanitized = normalizeWorkflowFormData(formData, workflow);
      await update({ _id, formData: sanitized, successMessage, errorMessage, onSuccess });
    } catch {
      // Erros já exibidos via toast pelos hooks de atualização.
    }
  };

  const validate = (values: IWorkflowInput) => {
    const errors: Record<string, string> = {};
    return errors;
  };

  return (
    <Formik
      initialValues={buildWorkflowFormInitialData(workflow)}
      validateOnChange={false}
      validate={validate}
      onSubmit={handleUpdate}
    >
      {({ values, errors, handleChange, submitForm }) => {
        return (
          <MDBox my={4}>
            <Grid size={12}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight={"bold"}>
                    {messages.tabWorkflowDataLabel}
                  </MDTypography>
                </MDBox>

                <MDBox component="form" pb={3} px={3}>
                  <Grid container spacing={4}>
                    <Grid
                      size={{ xs: 12, md: 4 }}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        borderRight: { md: "1px solid #e9ecef" },
                        pr: { md: 3 },
                      }}
                    >
                      <AgentAvatarImage
                        messages={messages}
                        workflowId={Number(workflow?.id) || 0}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 8 }}>
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormField
                            variant="standard"
                            name="name"
                            label={messages.nameLabel}
                            value={values.name}
                            onChange={handleChange}
                            fullWidth
                            error={!!errors.name}
                            required
                          />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormField
                            variant="standard"
                            name="displayName"
                            label={messages.displayNameLabel}
                            value={values.displayName}
                            onChange={handleChange}
                            fullWidth
                            error={!!errors.displayName}
                            required
                          />
                        </Grid>

                        <Grid size={12}>
                          <MDTypography
                            variant="caption"
                            color="text"
                            sx={{ display: "block", mb: 0.5 }}
                          >
                            {messages.descriptionLabel}
                          </MDTypography>
                          <TextField
                            name="description"
                            value={values.description}
                            multiline
                            minRows={4}
                            fullWidth
                            disabled
                            error={!!errors.description}
                            helperText={errors.description}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
            </Grid>

            <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
              <MDButton
                variant="gradient"
                color="light"
                onClick={() => router(ROUTE_URLS.workflow(lang))}
                isLoading={isLoading}
              >
                {messages.cancelButton}
              </MDButton>

              <MDButton variant="gradient" color="dark" onClick={submitForm} isLoading={isLoading}>
                {isLoading ? loadingIcon : messages.updateButton}
              </MDButton>
            </MDBox>
          </MDBox>
        );
      }}
    </Formik>
  );
}
